import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';
import type { GeneNodeData } from '@/utils/ontologyTree/types';

export type GeneFlowNode = Node<GeneNodeData, 'gene'>;

export const GeneNode = memo(function GeneNode({ id, data }: NodeProps<GeneFlowNode>) {
  const { label, fullName, hasRelatedDiseases, onClick, onCollapse } = data;

  return (
    <div
      style={{
        position: 'relative',
        background: '#f0faf3',
        border: '1.5px solid #2e7d32',
        borderRadius: 8,
        padding: '8px 12px',
        minWidth: 130,
        maxWidth: 230,
        boxShadow: '0 1px 4px rgba(46,125,50,0.1)',
        userSelect: 'none',
        transition: 'box-shadow 0.15s, background 0.15s',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#2e7d32', width: 7, height: 7 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          onClick={() => !hasRelatedDiseases && onClick?.(id)}
          role={!hasRelatedDiseases && onClick ? 'button' : undefined}
          tabIndex={!hasRelatedDiseases && onClick ? 0 : undefined}
          onKeyDown={!hasRelatedDiseases && onClick ? e => e.key === 'Enter' && onClick(id) : undefined}
          title={!hasRelatedDiseases ? 'Click to show related diseases' : undefined}
          style={{
            flex: 1,
            minWidth: 0,
            cursor: !hasRelatedDiseases && onClick ? 'pointer' : 'default',
          }}
          onMouseOver={e => {
            if (!hasRelatedDiseases && onClick)
              (e.currentTarget.parentElement as HTMLElement).style.background = '#d9f5e0';
          }}
          onMouseOut={e => {
            (e.currentTarget.parentElement as HTMLElement).style.background = '#f0faf3';
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#1b5e20',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={fullName ?? label}
          >
            {label}
          </div>
          {fullName && (
            <div
              style={{
                fontSize: 10,
                color: '#4caf50',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: 2,
              }}
              title={fullName}
            >
              {fullName.length > 28 ? `${fullName.slice(0, 26)}…` : fullName}
            </div>
          )}
        </div>

        {hasRelatedDiseases && onCollapse && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onCollapse(id);
            }}
            title="Collapse related diseases"
            aria-label="Collapse related diseases"
            style={{
              padding: '2px 8px',
              fontSize: 10,
              fontWeight: 500,
              border: '1px solid #2e7d32',
              borderRadius: 5,
              background: '#f0faf3',
              color: '#1b5e20',
              cursor: 'pointer',
              flexShrink: 0,
              lineHeight: 1.5,
              transition: 'background 0.12s',
            }}
            onMouseOver={e => (e.currentTarget.style.background = '#d9f5e0')}
            onMouseOut={e => (e.currentTarget.style.background = '#f0faf3')}
          >
            Collapse
          </button>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#2e7d32', width: 7, height: 7 }}
      />
    </div>
  );
});
